import React, { useState, useRef, useEffect, createContext, useContext } from "react";
import { useLocation, Link } from "react-router-dom";
import { translations, useLanguage } from "../utils/translations";
import FloatingSelfRegisterForm from "../components/FloatingSelfRegisterForm";

// Language Context
const LanguageContext = createContext();

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const { getStoredLanguage, setStoredLanguage } = useLanguage();
  const [language, setLanguage] = useState(getStoredLanguage());

  const changeLanguage = (lang) => {
    setLanguage(lang);
    setStoredLanguage(lang);
  };

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};

const HomeNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const location = useLocation();
  const aboutRef = useRef(null);
  const languageRef = useRef(null);
  const { language, changeLanguage, t } = useLanguageContext();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Toggle About dropdown on click
  const toggleAbout = () => {
    setAboutOpen(!aboutOpen);
  };

  // Toggle Language dropdown on click
  const toggleLanguage = () => {
    setLanguageOpen(!languageOpen);
  };

  // Close dropdown if click happens outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth >= 768) { // only for desktop
        if (aboutRef.current && !aboutRef.current.contains(event.target)) {
          setAboutOpen(false);
        }
        if (languageRef.current && !languageRef.current.contains(event.target)) {
          setLanguageOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper to check active link
  const isActive = (path) => location.pathname === path;

  // Base nav link style with underline animation
  const navLinkStyle = (path) =>
    `relative transition duration-300 ${
      isActive(path) ? "text-brown-600 font-bold" : "hover:text-gray-700"
    } 
    after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-full after:h-[2px] 
    after:bg-brown-600 after:scale-x-0 after:origin-left after:transition-transform after:duration-300
    ${
      isActive(path)
        ? "after:scale-x-100"
        : "hover:after:scale-x-100 after:bg-gray-600"
    }`;

  return (
    <nav className="bg-white sticky top-0 z-50 shadow">
      <div className="flex flex-col items-center">
        {/* Centered Logo */}
      
        <div className="flex items-center justify-between w-full p-4">
          <div className="flex items-center">
            <Link to="/" onClick={() => setIsOpen(false)} rel="noopener noreferrer">
              <img src="/aaapdmainlogo.jpg" alt="AAAPD-logo" className="h-[6.5rem] w-auto" width="400" height="104" />
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="flex-grow md:flex md:justify-center">
            <ul
              className={`flex ${
                isOpen ? "flex-col" : "hidden"
              } md:flex md:flex-row md:space-x-6 p-4 md:p-0`}
            >
              <li>
                <Link
                  to="/"
                  className={navLinkStyle("/")}
                  onClick={() => setIsOpen(false)}
                >
                  {t('home')}
                </Link>
              </li>

              {/* About Dropdown (desktop = absolute, mobile = accordion) */}
              {/* About Dropdown */}
              <li className="w-full md:w-auto">
                <div className="relative" ref={aboutRef}>
                  <button
                    onClick={toggleAbout}
                    className={`w-full md:w-auto text-left md:text-center transition duration-300 ${
                      location.pathname.includes("/about")
                        ? "text-brown-600 font-bold"
                        : "hover:text-gray-700"
                    } relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-full after:h-[2px] after:bg-brown-600 after:scale-x-0 after:origin-left after:transition-transform after:duration-300 ${
                      location.pathname.includes("/about")
                        ? "after:scale-x-100"
                        : "hover:after:scale-x-100 after:bg-gray-600"
                    }`}
                  >
                    {t('about')} ▾
                  </button>

                  {/* Desktop Dropdown */}
                  <div
                    className={`absolute top-full left-0 md:left-0 bg-white shadow-lg mt-2 rounded-lg z-50 hidden md:block transition-all duration-300 ${
                      aboutOpen ? "opacity-100 visible" : "opacity-0 invisible"
                    }`}
                    style={{ minWidth: "200px" }} // ensure dropdown has proper width
                  >
                    <ul className="flex flex-col p-2">
                      <li>
                        <Link
                          to="/whoweare"
                          className={`block px-4 py-2 transition duration-300 ${
                            isActive("/whoweare")
                              ? "bg-gray-100 text-brown-600 font-bold"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => {
                            setAboutOpen(false);
                            setIsOpen(false);
                          }}
                        >
                          {t('whoWeAre')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/about/mission-vision-values"
                          className={`block px-4 py-2 transition duration-300 ${
                            isActive("/about/mission-vision-values")
                              ? "bg-gray-100 text-brown-600 font-bold"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => {
                            setAboutOpen(false);
                            setIsOpen(false);
                          }}
                        >
                          {t('missionVisionValues')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/about/leadership"
                          className={`block px-4 py-2 transition duration-300 ${
                            isActive("/about/leadership")
                              ? "bg-gray-100 text-brown-600 font-bold"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => {
                            setAboutOpen(false);
                            setIsOpen(false);
                          }}
                        >
                          {t('leadership')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/annual-report"
                          className={`block px-4 py-2 transition duration-300 ${
                            isActive("/annual-report")
                              ? "bg-gray-100 text-brown-600 font-bold"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => {
                            setAboutOpen(false);
                            setIsOpen(false);
                          }}
                        >
                          {t('annualreport')}
                        </Link>
                      </li>
                    </ul>
                  </div>

                  {/* Mobile Accordion */}
                  <div
                    className={`md:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out ${
                      aboutOpen ? "max-h-64" : "max-h-0"
                    }`}
                  >
                    <ul className="flex flex-col pl-4">
                      <li>
                        <Link
                          to="/whoweare"
                          className={`block py-2 transition duration-300 ${
                            isActive("/whoweare")
                              ? "text-brown-600 font-bold"
                              : "hover:text-gray-700"
                          }`}
                          onClick={() => {
                            setAboutOpen(false);
                            setIsOpen(false);
                          }}
                        >
                          {t('whoWeAre')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/about/mission-vision-values"
                          className={`block py-2 transition duration-300 ${
                            isActive("/about/mission-vision-values")
                              ? "text-brown-600 font-bold"
                              : "hover:text-gray-700"
                          }`}
                          onClick={() => {
                            setAboutOpen(false);
                            setIsOpen(false);
                          }}
                        >
                          {t('missionVisionValues')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/about/leadership"
                          className={`block py-2 transition duration-300 ${
                            isActive("/about/leadership")
                              ? "text-brown-600 font-bold"
                              : "hover:text-gray-700"
                          }`}
                          onClick={() => {
                            setAboutOpen(false);
                            setIsOpen(false);
                          }}
                        >
                          {t('leadership')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/annual-report"
                          className={`block py-2 transition duration-300 ${
                            isActive("/annual-report")
                              ? "text-brown-600 font-bold"
                              : "hover:text-gray-700"
                          }`}
                          onClick={() => {
                            setAboutOpen(false);
                            setIsOpen(false);
                          }}
                        >
                          {t('annualreport')}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </li>


              <li>
                <Link
                  to="/programs"
                  className={navLinkStyle("/programs")}
                  onClick={() => setIsOpen(false)}
                >
                  {t('programs')}
                </Link>
              </li>

              <li>
                <Link
                  to="/gallery"
                  className={navLinkStyle("/gallery")}
                  onClick={() => setIsOpen(false)}
                >
                  {t('gallery')}
                </Link>
              </li>

              <li>
                <Link
                  to="/memberss"
                  className={navLinkStyle("/memberss")}
                  onClick={() => setIsOpen(false)}
                >
                  {t('Partnerss')}
                </Link>
              </li>

          

              <li>
                <Link
                  to="/news"
                  className={navLinkStyle("/news")}
                  onClick={() => setIsOpen(false)}
                >
                  {t('news')}
                </Link>
              </li>
              <li>
                <Link
                  to="/disability-policies-research"
                  className={navLinkStyle("/disability-policies-research")}
                  onClick={() => setIsOpen(false)}
                >
                  {t('resources')}
                </Link>
              </li>

              <li>
                <Link
                  to="/contactus"
                  className={navLinkStyle("/contactus")}
                  onClick={() => setIsOpen(false)}
                >
                  {t('contactUs')}
                </Link>
              </li>
              <li>
                <Link
                  to="/donate"
                  className={navLinkStyle("/donate")}
                  onClick={() => setIsOpen(false)}
                >
                 {t('donate')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Language Dropdown */}
          <div className="flex items-center ml-4">
            <div className="relative" ref={languageRef}>
              <button
                onClick={toggleLanguage}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brown-500 transition duration-300"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                {language === 'en' ? 'Eng' : 'አማ'}
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Language Dropdown Menu */}
              <div
                className={`absolute right-0 top-full mt-2 w-24 bg-white border border-gray-200 rounded-md shadow-lg z-50 transition-all duration-300 ${
                  languageOpen ? "opacity-100 visible" : "opacity-0 invisible"
                }`}
              >
                <div className="py-1">
                  <button
                    onClick={() => {
                      changeLanguage('en');
                      setLanguageOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition duration-300 ${
                      language === 'en'
                        ? 'bg-brown-50 text-brown-600 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Eng
                  </button>
                  <button
                    onClick={() => {
                      changeLanguage('am');
                      setLanguageOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition duration-300 ${
                      language === 'am'
                        ? 'bg-brown-50 text-brown-600 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    አማ
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right-side Logos */}
          

          {/* Login Button */}
          <div className="ml-4">
            <Link
              to="/login"
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 rounded text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{ backgroundColor: "#6b4f3b" }}
            >
              {t('login')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-brown-600 focus:outline-none"
            onClick={toggleMenu}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Self Registration Floating Button - Available on all pages */}
      <FloatingSelfRegisterForm />
    </nav>
  );
};

export default HomeNav;
